import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { useDebounce } from "../../hooks/useDebounce";
import { formApi } from "./api";
import type { FeedbackForm } from "./contract";

export function FormRewardSettings({
  form,
  onChange,
  canRewards,
  canEvents,
  locked,
}: {
  form: FeedbackForm;
  onChange: (change: Partial<FeedbackForm>) => void;
  canRewards: boolean;
  canEvents: boolean;
  locked: boolean;
}) {
  const [search, setSearch] = useState(""),
    [options, setOptions] = useState<{
      awards: { id: number; name: string }[];
      prefixes: { id: number; name: string }[];
    }>({ awards: [], prefixes: [] }),
    [error, setError] = useState(""),
    [loading, setLoading] = useState(false);
  const query = useDebounce(search, 250);
  const [kind, setKind] = useState(form.reward_kind);
  useEffect(() => setKind(form.reward_kind), [form.reward_kind]);
  useEffect(() => {
    if (!canRewards || !["award", "prefix"].includes(kind)) return;
    let stop = false;
    setLoading(true);
    formApi
      .rewards(query)
      .then((r) => {
        if (!stop) {
          setOptions(r);
          setError("");
        }
      })
      .catch((e) => {
        if (!stop) setError(e.message);
      })
      .finally(() => {
        if (!stop) setLoading(false);
      });
    return () => {
      stop = true;
    };
  }, [query, canRewards, kind]);
  const choices = kind === "award" ? options.awards : options.prefixes;
  const restricted =
    locked || (!canRewards && ["award", "prefix"].includes(form.reward_kind));
  return (
    <section className="glass-card p-5 space-y-4" data-tour="forms-rewards">
      <div className="flex gap-3 items-start">
        <Gift className="w-5 h-5 text-accent-300 shrink-0" />
        <div>
          <h2 className="font-medium text-sm">Completion reward</h2>
          <p className="text-xs text-glass-400 mt-1">
            An optional thank-you, delivered automatically after a valid
            response.
          </p>
        </div>
      </div>
      <fieldset disabled={restricted} className="space-y-3 disabled:opacity-60">
        <label className="block text-xs space-y-2">
          <span className="text-glass-400">Reward type</span>
          <select
            className="glass-select w-full"
            value={kind}
            onChange={(e) => {
              const next = e.target.value as FeedbackForm["reward_kind"];
              setKind(next);
              setSearch("");
              if (next === "none" || next === "event_point")
                onChange({
                  reward_kind: next,
                  reward_id: null,
                  reward_label: next === "event_point" ? "1 event point" : "",
                  award_points: next === "event_point",
                  ...(next === "event_point" ? { after_event: true } : {}),
                });
            }}
          >
            <option value="none">No reward</option>
            <option value="event_point" disabled={!canEvents || !form.event_id}>
              1 event point{!form.event_id ? " · link an event first" : ""}
            </option>
            <option value="award" disabled={!canRewards}>
              Tesseract award
            </option>
            <option value="prefix" disabled={!canRewards}>
              Minecraft prefix
            </option>
          </select>
        </label>
        {["award", "prefix"].includes(kind) && (
          <>
            <input
              aria-label="Search rewards"
              className="glass-input w-full"
              placeholder={`Search ${kind === "award" ? "awards" : "prefixes"}…`}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              disabled={!canRewards}
            />
            <label className="block text-xs space-y-2">
              <span className="text-glass-400">
                {kind === "award" ? "Award" : "Prefix"}
              </span>
              <select
                className="glass-select w-full"
                aria-label={kind === "award" ? "Award" : "Prefix"}
                value={kind === form.reward_kind ? form.reward_id || "" : ""}
                disabled={!canRewards}
                onChange={(e) => {
                  const choice = choices.find(
                    (c) => c.id === Number(e.target.value),
                  );
                  if (choice)
                    onChange({
                      reward_kind: kind,
                      reward_id: choice.id,
                      reward_label: choice.name,
                      award_points: false,
                    });
                }}
              >
                <option value="" disabled>
                  {loading ? "Loading rewards…" : "Choose a reward…"}
                </option>
                {kind === form.reward_kind &&
                  form.reward_id &&
                  !choices.some((c) => c.id === form.reward_id) && (
                    <option value={form.reward_id}>{form.reward_label}</option>
                  )}
                {choices.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </label>
            {kind !== form.reward_kind && (
              <p className="text-xs text-amber-300">
                Choose a reward to apply this change. Current reward:{" "}
                {form.reward_label || "none"}.
              </p>
            )}
            {kind === "prefix" && (
              <p className="text-xs text-glass-400">
                Available cosmetic prefixes. Prefixes that grant staff or
                permission groups are excluded.
              </p>
            )}
            {!loading && !choices.length && (
              <p className="text-xs text-glass-400">
                No rewards match this search.
              </p>
            )}
          </>
        )}
      </fieldset>
      {error && (
        <p role="alert" className="text-xs text-red-400">
          {error}
        </p>
      )}
      <p className="text-xs text-glass-400">
        {locked
          ? "Reward settings are locked after the first response."
          : kind === "event_point"
            ? "One feedback point per player per event, including across multiple forms."
            : kind === "none"
              ? "Players can respond without receiving a reward."
              : "Each reward is granted once. Players who already own it keep their existing reward."}
      </p>
      {!canRewards && (
        <p className="text-xs text-glass-400">
          Form administrators and staff with reward management access can
          configure awards and prefixes.
        </p>
      )}
    </section>
  );
}
